// JWT is persisted in localStorage with a 24h TTL so that a user who passed
// OTP in the last day can return to the gate (or refresh) without re-verifying.
// In-memory mirror avoids repeated localStorage reads during a single session.

const STORAGE_KEY = "bc-gate-jwt";
const TTL_MS = 24 * 60 * 60 * 1000;

interface StoredToken {
  token: string;
  expiresAt: number;
}

let memoryToken: string | null = null;

function readStorage(): StoredToken | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredToken;
    if (typeof parsed?.token !== "string" || typeof parsed?.expiresAt !== "number") return null;
    if (parsed.expiresAt <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(token: string): void {
  try {
    const payload: StoredToken = { token, expiresAt: Date.now() + TTL_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private-browsing or quota: degrade to in-memory only.
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

export function setToken(next: string): void {
  memoryToken = next;
  writeStorage(next);
}

export function getToken(): string | null {
  if (memoryToken) return memoryToken;
  const stored = readStorage();
  if (stored) {
    memoryToken = stored.token;
    return memoryToken;
  }
  return null;
}

export function clearToken(): void {
  memoryToken = null;
  clearStorage();
}

export function hasValidToken(): boolean {
  return getToken() !== null;
}
